import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { 
  Target, 
  Plus, 
  Trash2, 
  Bell, 
  BellOff,
  MapPin,
  AlertTriangle,
  Shield,
  CloudLightning,
  Radio
} from 'lucide-react';

// Watch Zone Manager Component
const WatchZoneManager = ({ 
  watchZones, 
  setWatchZones, 
  userLocation,
  onZoneSelect 
}) => {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [newZoneName, setNewZoneName] = useState('');
  const [newZoneRadius, setNewZoneRadius] = useState(5); // miles
  const [selectedAlertTypes, setSelectedAlertTypes] = useState(['scanners', 'weather', 'offenders']);
  
  // Create a new watch zone centered on user location or map center
  const createZone = useCallback(() => {
    if (!newZoneName.trim()) {
      toast({
        title: 'Name Required',
        description: 'Please enter a name for your watch zone.',
        variant: 'destructive'
      });
      return;
    }
    
    const location = userLocation || { lat: 34.0522, lng: -118.2437 }; // Default to LA
    
    const newZone = {
      id: `zone-${Date.now()}`,
      name: newZoneName.trim(),
      center: location,
      radius: newZoneRadius,
      alertTypes: selectedAlertTypes,
      enabled: true,
      createdAt: new Date().toISOString(),
      alertCount: 0
    };
    
    setWatchZones(prev => [...prev, newZone]);
    setNewZoneName('');
    setNewZoneRadius(5);
    setIsOpen(false);
    
    toast({
      title: 'Watch Zone Created',
      description: `"${newZone.name}" is now monitoring a ${newZoneRadius} mile radius.`
    });
  }, [newZoneName, newZoneRadius, selectedAlertTypes, userLocation, setWatchZones, toast]);
  
  // Toggle zone enabled state
  const toggleZone = useCallback((zoneId) => {
    setWatchZones(prev => prev.map(zone => 
      zone.id === zoneId ? { ...zone, enabled: !zone.enabled } : zone
    ));
  }, [setWatchZones]);
  
  // Delete a zone
  const deleteZone = useCallback((zoneId) => {
    setWatchZones(prev => prev.filter(zone => zone.id !== zoneId));
    toast({
      title: 'Zone Deleted',
      description: 'Watch zone has been removed.'
    });
  }, [setWatchZones, toast]);
  
  // Toggle alert type
  const toggleAlertType = (type) => {
    setSelectedAlertTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };
  
  const alertTypeOptions = [
    { key: 'scanners', label: 'Scanner Activity', icon: Radio, color: 'teal' },
    { key: 'weather', label: 'Weather Alerts', icon: CloudLightning, color: 'amber' },
    { key: 'offenders', label: 'Sex Offenders', icon: Shield, color: 'red' },
    { key: 'incidents', label: 'Incidents', icon: AlertTriangle, color: 'orange' }
  ];
  
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon"
          className="relative bg-slate-900/80 border border-slate-700 hover:bg-slate-800"
          data-testid="watch-zones-trigger"
        >
          <Target className="w-5 h-5 text-teal-400" />
          {watchZones.length > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-teal-500 rounded-full text-[10px] text-white flex items-center justify-center">
              {watchZones.length}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="glass-card border-slate-800 w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-teal-400" />
            Watch Zones
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            Monitor specific areas and receive alerts when events occur nearby.
          </SheetDescription>
        </SheetHeader>
        
        <div className="mt-6 space-y-4">
          {/* Create New Zone Dialog */}
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button 
                className="w-full bg-teal-600 hover:bg-teal-500 text-white"
                data-testid="create-zone-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Watch Zone
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-card border-slate-700">
              <DialogHeader>
                <DialogTitle className="text-white">Create Watch Zone</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Define an area to monitor for alerts.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">Zone Name</label>
                  <Input
                    value={newZoneName}
                    onChange={(e) => setNewZoneName(e.target.value)}
                    placeholder="e.g., Home, Work, School"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="zone-name-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">
                    Radius: {newZoneRadius} miles
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="25"
                    value={newZoneRadius}
                    onChange={(e) => setNewZoneRadius(Number(e.target.value))}
                    className="w-full accent-teal-500"
                    data-testid="zone-radius-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>1 mi</span>
                    <span>25 mi</span>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">Alert Types</label>
                  <div className="grid grid-cols-2 gap-2">
                    {alertTypeOptions.map(opt => {
                      const Icon = opt.icon;
                      const isSelected = selectedAlertTypes.includes(opt.key);
                      return (
                        <button
                          key={opt.key}
                          onClick={() => toggleAlertType(opt.key)}
                          className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${
                            isSelected 
                              ? 'bg-teal-600/20 border-teal-500 text-teal-400'
                              : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                          }`}
                          data-testid={`alert-type-${opt.key}`}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="text-xs">{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <MapPin className="w-4 h-4 text-teal-400" />
                    <span>Zone will be centered on your current location</span>
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="ghost" onClick={() => setIsOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={createZone}
                  className="bg-teal-600 hover:bg-teal-500"
                  data-testid="confirm-create-zone"
                >
                  Create Zone
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Existing Zones List */}
          {watchZones.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No watch zones yet</p>
              <p className="text-xs mt-1">Create a zone to start monitoring an area</p>
            </div>
          ) : (
            <div className="space-y-3">
              {watchZones.map(zone => (
                <div 
                  key={zone.id}
                  className={`p-4 rounded-lg border transition-all ${
                    zone.enabled 
                      ? 'bg-slate-800/50 border-teal-500/30'
                      : 'bg-slate-900/50 border-slate-700 opacity-60'
                  }`}
                  data-testid={`zone-${zone.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        zone.enabled ? 'bg-teal-600/20' : 'bg-slate-800'
                      }`}>
                        <Target className={`w-5 h-5 ${zone.enabled ? 'text-teal-400' : 'text-slate-500'}`} />
                      </div>
                      <div>
                        <h4 className="text-white font-medium">{zone.name}</h4>
                        <p className="text-xs text-slate-400">{zone.radius} mile radius</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleZone(zone.id)}
                        className={`p-2 rounded-lg transition-all ${
                          zone.enabled 
                            ? 'bg-teal-600/20 text-teal-400 hover:bg-teal-600/30'
                            : 'bg-slate-800 text-slate-500 hover:bg-slate-700'
                        }`}
                        title={zone.enabled ? 'Disable alerts' : 'Enable alerts'}
                        data-testid={`toggle-zone-${zone.id}`}
                      >
                        {zone.enabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => deleteZone(zone.id)}
                        className="p-2 rounded-lg bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-all"
                        title="Delete zone"
                        data-testid={`delete-zone-${zone.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Alert Types */}
                  <div className="flex flex-wrap gap-1 mt-3">
                    {zone.alertTypes.map(type => {
                      const opt = alertTypeOptions.find(o => o.key === type);
                      if (!opt) return null;
                      const Icon = opt.icon;
                      return (
                        <Badge 
                          key={type}
                          variant="secondary"
                          className="bg-slate-700 text-slate-300 text-[10px]"
                        >
                          <Icon className="w-3 h-3 mr-1" />
                          {opt.label}
                        </Badge>
                      );
                    })}
                  </div>
                  
                  {/* Stats */}
                  {zone.alertCount > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-700">
                      <p className="text-xs text-slate-400">
                        <span className="text-amber-400 font-medium">{zone.alertCount}</span> alerts in last 24h
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default WatchZoneManager;
